/**
 * ItemSwitcher 模块
 * 
 * 该模块用于实现一个基于 hover 事件的切换效果，支持多步切换、防抖处理和动态动画时长控制。
 * 适用于需要逐步切换元素的场景，例如服务卡片、轮播图等。
 * 
 * 主要功能：
 * 1. 支持多步切换，逐步激活目标元素。
 * 2. 防抖处理，避免频繁触发切换逻辑。
 * 3. 动态更新 CSS 变量，控制切换动画时长。
 * 4. 支持自定义配置，如总切换时间、防抖等待时间、激活类名等。
 * 
 * 使用示例：
 * const switcher = new ItemSwitcher(".section-service", {
 *     totalTime: 800, // 总切换时间
 *     debounceWait: 100, // 防抖等待时间
 *     action:"hover", // 触发切换的事件类型，默认为 hover:hover,click
 *     activeClass: "active", // 激活状态的类名
 *     animationDurationVar: "--custom-animation-duration", // 自定义 CSS 变量名
 * });
 * 使用示例：背景逐步无缝切换效果
 * 一、HTML结构：
 <div class="item-loop">
    <div class="item"></div>
    <div class="item"></div>
    <div class="item"></div>
 </div>
 * 二、CSS样式：
 :root {
    --item-loop-duration: 800ms; // 切换动画时长
 }
.item {
    linear-gradient(to right, #f2f2f2 33.333%, transparent 33.333%), //左侧纯色
    linear-gradient(to left, #f2f2f2 33.333%, transparent 33.333%), //右侧纯色
    url("../images/item-image-bg.jpg")  center center / 33.333% auto; // 图片背景
    background-repeat: no-repeat;
    background-size: 310% 100%; // 整个背景大小，需稍大于3倍大小，以便左右两侧纯色平滑过渡
    background-position:left; // 默认的背景为左侧纯色背景
    transition: background-position var(--animation-service-duration) linear;
    &.active{
        background-position: center; // 背景居中
        & ~ .item {
            background-position: right; //active元素右侧所有的兄弟元素背景均使用右侧背景
        }
    }
}
 * 定义css变量 --animation-switcher-duration，值为切换动画时长，单位为毫秒
 * 设置三段背景：左右为相同的纯色背景，中间为图片，使用less:
 
 */
class itemStepSwitcher {
    constructor(containerSelector, options = {}) {
        // 默认配置
        this.defaultOptions = {
            totalTime: 800, // 总切换时间（单位：毫秒）
            debounceWait: 100, // 防抖等待时间（单位：毫秒）
            action: "hover,click", // 触发切换的事件类型，默认为 hover:hover,click
            activeClass: "active", // 激活状态的类名
            animationDurationVar: "--animation-switcher-duration", // CSS 变量名，用于控制动画时长
        };

        // 合并用户自定义配置和默认配置
        this.options = { ...this.defaultOptions, ...options };

        // 获取容器元素-如果页面中不存在，则直接返回
        this.container = $(containerSelector);
        if (!this.container.length) {
            console.error(`容器元素未找到: step-switcher:${containerSelector}`);
            return;
        }

        // 初始化时检查是否有元素已经具有 activeClass，如果没有，则默认将第一个元素设置为激活状态
        if (!this.container.find(`.${this.options.activeClass}`).length) {
            this.container.find(".item").first().addClass(this.options.activeClass);
        }

        // 状态变量
        this.lastHoverIndex = -1; // 记录最后一个 hover 事件的目标元素索引
        this.isSwitching = false; // 是否正在切换
        this.switchTimeout = null; // 切换定时器
        this.lastPendingSwitch = null; // 记录最后一次的切换请求

        // 防抖后的切换函数
        this.debounceSwitchItem = this.debounce((that, index, oldIndex) => {
            // 更新最后一个 hover 事件的目标元素索引
            this.lastHoverIndex = index;
            console.log(`最后一个 hover 事件的目标元素索引是：`, this.lastHoverIndex);
            // 调用切换函数
            this.switchItem(that, index, oldIndex);
        }, this.options.debounceWait);

        // 初始化事件绑定
        this.initEvents();
    }

    // 初始化事件绑定
    initEvents() {
        // 为容器内的每个 .item 元素绑定 hover 事件
        const action = this.options.action.split(",");
        console.log("初始化事件绑定", action);

        if (action.includes("hover")) {
            console.log("初始化 hover 事件");
            this.container.find(".item").hover(
                (event) => this.handleHover(event), // 鼠标进入时触发
                () => { } // 鼠标离开时的逻辑（可选）
            );
        }
        if (action.includes("click")) {
            console.log("初始化 click 事件");
            this.container.find(".item").click(
                (event) => this.handleHover(event), // 鼠标点击时触发
            );
        }
    }

    // 处理 hover 事件
    handleHover(event) {
        const that = $(event.currentTarget); // 当前 hover 的元素
        const oldIndex = that.siblings(`.${this.options.activeClass}`).index(); // 当前激活元素的索引
        const index = that.index(); // 当前 hover 元素的索引
        console.log(`原来的 active 元素的序号是：`, oldIndex);
        console.log(`当前要添加 active 的序号是：`, index);

        // 调用防抖函数，避免频繁触发
        this.debounceSwitchItem(that, index, oldIndex);
    }

    // 切换函数
    switchItem(querySelector, now, old) {
        // 如果正在切换，则记录最后一次的切换请求并返回
        if (this.isSwitching) {
            console.log("正在切换，记录最后一次请求");
            this.lastPendingSwitch = { querySelector, now, old };
            return;
        }

        // 标记为正在切换
        this.isSwitching = true;

        console.log(`要切换的元素的序号是：`, now);
        const step = now - old; // 计算需要切换的步数
        console.log(`要切换的步数是：`, step);

        const items = querySelector.parent().find(".item"); // 获取所有 .item 元素
        const totalItems = items.length; // 元素总数

        // 确保 now 和 old 在有效范围内
        //console.log(now, old, totalItems);
        if (now < 0 || now >= totalItems || old < 0 || old >= totalItems) {
            console.error("索引超出范围",old, now, totalItems);
            this.isSwitching = false; // 释放锁
            return;
        }

        // 清除之前的切换定时器
        clearTimeout(this.switchTimeout);

        // 计算每次切换的间隔时间
        const stepCount = Math.abs(step); // 切换步数
        const intervalTime = this.options.totalTime / stepCount; // 每次切换的间隔时间

        // 动态更新 CSS 变量，控制动画时长
        document.documentElement.style.setProperty(
            this.options.animationDurationVar, // 使用配置的 CSS 变量名
            `${intervalTime}ms`
        );

        // 确定切换方向（1 表示向右，-1 表示向左）
        const direction = step > 0 ? 1 : -1;

        // 当前索引
        let current = old;

        // 逐步切换
        const stepSwitch = () => {
            // 如果当前步骤完成后有最后一次切换请求，则终止当前切换过程
            if (this.lastPendingSwitch) {
                console.log("检测到最后一次切换请求，终止当前切换过程");
                this.isSwitching = false; // 释放锁

                // 执行最后一次切换请求
                const { querySelector, now, old } = this.lastPendingSwitch;
                this.lastPendingSwitch = null; // 清空最后一次请求
                this.switchItem(querySelector, now, old); // 执行最后一次切换
                return;
            }

            // 如果当前切换完成，则结束
            if (current === now) {
                console.log("当前切换完成");
                this.isSwitching = false; // 切换完成，释放锁
                return;
            }

            // 更新当前索引
            current += direction;

            // 移除当前 active 类
            items.removeClass(this.options.activeClass);

            // 为下一个元素添加 active 类
            items.eq(current).addClass(this.options.activeClass);

            // 延迟 intervalTime 后继续切换
            this.switchTimeout = setTimeout(stepSwitch, intervalTime);
        };

        // 开始切换
        stepSwitch();
    }

    // 防抖函数
    debounce(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;
            clearTimeout(timeout); // 清除之前的定时器
            timeout = setTimeout(() => func.apply(context, args), wait); // 设置新的定时器
        };
    }
}

// 导出 itemStepSwitcher 类
export default itemStepSwitcher;